import { spotifyAuthClient } from './spotify-auth'

/**
 * The Web Playback SDK registers this browser as a Spotify Connect device. It
 * is the fallback device: the native app survives screen lock, a browser tab
 * often does not (notably on iOS).
 */

const SDK_URL = 'https://sdk.scdn.co/spotify-player.js'

interface SpotifyPlayer {
  connect(): Promise<boolean>
  disconnect(): void
  activateElement(): Promise<void>
  addListener(event: string, callback: (payload: unknown) => void): boolean
}

interface SpotifyPlayerConstructor {
  new (options: {
    name: string
    getOAuthToken: (callback: (token: string) => void) => void
    volume?: number
  }): SpotifyPlayer
}

interface SpotifyNamespace {
  Player: SpotifyPlayerConstructor
}

declare global {
  interface Window {
    Spotify?: SpotifyNamespace
    onSpotifyWebPlaybackSDKReady?: () => void
  }
}

let sdkPromise: Promise<SpotifyNamespace> | null = null

function loadSdk(): Promise<SpotifyNamespace> {
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise<SpotifyNamespace>((resolve, reject) => {
    if (window.Spotify) {
      resolve(window.Spotify)
      return
    }
    window.onSpotifyWebPlaybackSDKReady = () => {
      if (window.Spotify) resolve(window.Spotify)
      else reject(new Error('Spotify SDK loaded without a Player'))
    }
    const script = document.createElement('script')
    script.src = SDK_URL
    script.async = true
    script.onerror = () => reject(new Error('Could not load the Spotify Web Playback SDK'))
    document.head.appendChild(script)
  })
  return sdkPromise
}

export interface BrowserPlayerHandle {
  deviceId: string
  /** Must be called from a user gesture before audio can start. */
  activate(): Promise<void>
  disconnect(): void
}

export type PlaybackErrorKind = 'account' | 'authentication' | 'initialization' | 'playback'

export interface BrowserPlayerCallbacks {
  onError?: (kind: PlaybackErrorKind, message: string) => void
  onStateChange?: (isPlaying: boolean) => void
}

function readMessage(payload: unknown): string {
  if (typeof payload === 'object' && payload !== null && 'message' in payload) {
    const message = (payload as { message: unknown }).message
    if (typeof message === 'string') return message
  }
  return 'Unknown playback error'
}

/** Connects this browser as a Connect device and resolves with its device ID. */
export async function createBrowserPlayer(
  deviceName: string,
  callbacks: BrowserPlayerCallbacks = {},
): Promise<BrowserPlayerHandle> {
  const sdk = await loadSdk()
  const player = new sdk.Player({
    name: deviceName,
    getOAuthToken: (callback) => {
      spotifyAuthClient
        .getAccessToken()
        .then(callback)
        .catch(() => callbacks.onError?.('authentication', 'Spotify session expired'))
    },
    volume: 1,
  })

  player.addListener('account_error', (payload) =>
    callbacks.onError?.('account', readMessage(payload)),
  )
  player.addListener('authentication_error', (payload) =>
    callbacks.onError?.('authentication', readMessage(payload)),
  )
  player.addListener('initialization_error', (payload) =>
    callbacks.onError?.('initialization', readMessage(payload)),
  )
  player.addListener('playback_error', (payload) =>
    callbacks.onError?.('playback', readMessage(payload)),
  )
  player.addListener('player_state_changed', (payload) => {
    if (typeof payload === 'object' && payload !== null && 'paused' in payload) {
      callbacks.onStateChange?.((payload as { paused: unknown }).paused !== true)
    }
  })

  const deviceId = await new Promise<string>((resolve, reject) => {
    player.addListener('ready', (payload) => {
      if (typeof payload === 'object' && payload !== null && 'device_id' in payload) {
        const id = (payload as { device_id: unknown }).device_id
        if (typeof id === 'string') {
          resolve(id)
          return
        }
      }
      reject(new Error('Spotify SDK became ready without a device ID'))
    })
    player.connect().then((connected) => {
      if (!connected) reject(new Error('Could not connect the Spotify player'))
    }, reject)
  })

  return {
    deviceId,
    activate: () => player.activateElement(),
    disconnect: () => player.disconnect(),
  }
}
